import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import apiUrls from "../../../../../api";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface GptConversation {
  id: number;
  conversation_metadata: {
    model?: string;
    userPrompts?: string[];
    userPromptsDebrief?: string[];
  };
  conversation_data: {
    output?: string[];
    outputDebrief?: string[];
  };
}

interface PeopleAIDetailProps {
  data: {
    aiConversationId?: number;
    // Legacy fields for backward compatibility
    conversation?: Message[];
    transcript?: string;
    summary?: string;
  };
  onLlmLoaded?: (llmName: string) => void;
  score?: number;
  scoreMax?: number | null;
}

const PeopleAIDetail = ({ data, onLlmLoaded, score, scoreMax }: PeopleAIDetailProps) => {
  const { t } = useTranslation();
  const [conversation, setConversation] = useState<GptConversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedIdRef = useRef<number | null>(null);

  // Get LLM name from model
  const getLlmName = (model: string): string => {
    if (model.toLowerCase().includes("mistral")) return "Mistral";
    if (model.toLowerCase().includes("gpt-4")) return "GPT-4";
    if (model.toLowerCase().includes("gpt-3")) return "GPT-3.5";
    if (model.toLowerCase().includes("claude")) return "Claude";
    return model || "-";
  };

  // Fetch conversation data from API
  useEffect(() => {
    const fetchConversation = async () => {
      // Don't fetch if ID is missing or invalid (e.g., -1)
      if (!data.aiConversationId || data.aiConversationId < 0) return;

      // Prevent duplicate fetches (React StrictMode)
      if (fetchedIdRef.current === data.aiConversationId) return;
      fetchedIdRef.current = data.aiConversationId;

      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${apiUrls.stats.gptConversationData}?conversationId=${data.aiConversationId}`,
        );
        // Note: axios interceptor in auth.ts auto-unwraps response.data.data to response.data
        setConversation(response.data);

        // Notify parent of LLM name
        if (onLlmLoaded && response.data?.conversation_metadata?.model) {
          onLlmLoaded(getLlmName(response.data.conversation_metadata.model));
        }
      } catch (err) {
        console.error("Error fetching conversation:", err);
        setError(t("statsNew.interaction.peopleAI.fetchError"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.aiConversationId]);

  // Build messages from conversation data
  const buildMessages = (): Message[] => {
    if (!conversation) return [];

    const { userPrompts = [] } = conversation.conversation_metadata;
    const { output = [] } = conversation.conversation_data;

    const messages: Message[] = [];

    // First output is the initial assistant message (before any user input)
    if (output[0]) {
      const cleaned = cleanMessage(output[0]);
      if (cleaned) {
        messages.push({ role: "assistant", content: cleaned });
      }
    }

    // Then alternate: userPrompts[1] -> output[1], userPrompts[2] -> output[2], etc.
    // (userPrompts[0] is the system prompt, not a user message)
    for (let i = 1; i < Math.max(userPrompts.length, output.length); i++) {
      if (userPrompts[i]) {
        const userContent = userPrompts[i].trim();
        if (userContent) {
          messages.push({ role: "user", content: userContent });
        }
      }
      if (output[i]) {
        const cleaned = cleanMessage(output[i]);
        if (cleaned) {
          messages.push({ role: "assistant", content: cleaned });
        }
      }
    }

    return messages;
  };

  // Build debrief messages - returns { initialDebrief, followUpMessages }
  const buildDebriefData = (): { initialDebrief: string | null; followUpMessages: Message[] } => {
    if (!conversation) return { initialDebrief: null, followUpMessages: [] };

    const { userPromptsDebrief = [] } = conversation.conversation_metadata;
    const { outputDebrief = [] } = conversation.conversation_data;

    // First output is the initial debrief (special styling)
    const initialDebrief = outputDebrief[0] ? cleanMessage(outputDebrief[0]) : null;

    // Rest of the conversation
    const followUpMessages: Message[] = [];

    for (let i = 1; i < Math.max(userPromptsDebrief.length, outputDebrief.length); i++) {
      // User messages start from index 1 in userPromptsDebrief (index 0 is the system prompt)
      if (userPromptsDebrief[i]) {
        const userContent = userPromptsDebrief[i].trim();
        if (userContent) {
          followUpMessages.push({ role: "user", content: userContent });
        }
      }
      if (outputDebrief[i]) {
        const cleaned = cleanMessage(outputDebrief[i]);
        if (cleaned) {
          followUpMessages.push({ role: "assistant", content: cleaned });
        }
      }
    }

    return { initialDebrief, followUpMessages };
  };

  // Clean message from special tags
  const cleanMessage = (message: string): string => {
    return message
      .replace(/\$\(MESSAGE_\d+\/\d+\)/g, "")
      .replace(/\$\(INTERRUPT_\d+\)/g, "")
      .replace(/\$\(SUCCESS\)/g, "")
      .replace(/\$\(FAIL\)/g, "")
      .replace(/\$\(CLOSE\)/g, "")
      .replace(/\$\(SILENCE\)/g, "")
      .replace(/\$\(SPEECH_INTERRUPTED\)/g, "")
      .trim();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return <div className="text-center py-8 text-red-500">{error}</div>;
  }

  // No conversation ID provided or invalid ID - show legacy data or empty state
  if (!data.aiConversationId || data.aiConversationId < 0) {
    // Try legacy format
    if (data.conversation && data.conversation.length > 0) {
      return (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {t("statsNew.interaction.peopleAI.conversation")}
          </h4>
          <div className="space-y-3">
            {data.conversation.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                    message.role === "user"
                      ? "bg-green-600 text-white rounded-br-md"
                      : "bg-gray-100 text-gray-900 rounded-bl-md"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  const messages = buildMessages();
  const { initialDebrief, followUpMessages } = buildDebriefData();
  const hasDebrief = initialDebrief !== null;

  // Determine success/failure based on score
  const isSuccess =
    score !== undefined && scoreMax !== undefined && scoreMax !== null && score >= scoreMax;
  const isFailure =
    score !== undefined && scoreMax !== undefined && scoreMax !== null && score < scoreMax;

  return (
    <div className="h-full flex flex-col">
      {/* Success/Failure indicator banner */}
      {(isSuccess || isFailure) && (
        <div
          className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg mb-3 ${
            isSuccess ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
          }`}
        >
          {isSuccess ? (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-sm font-medium text-green-700">
                {t("statsNew.interaction.peopleAI.success")}
              </span>
            </>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-sm font-medium text-red-700">
                {t("statsNew.interaction.peopleAI.failure")}
              </span>
            </>
          )}
        </div>
      )}

      {/* Two columns layout - takes all available height */}
      <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
        {/* Left column: Exchange transcript */}
        <div className="flex flex-col min-h-0">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 pb-2 border-b border-gray-200 flex-shrink-0">
            {t("statsNew.interaction.peopleAI.exchangeTranscript")}
          </h4>
          <div className="space-y-3 overflow-y-auto flex-1 pr-2">
            {messages.length === 0 ? (
              <p className="text-sm text-gray-500 italic">{t("statsNew.interaction.noData")}</p>
            ) : (
              messages.map((message, index) => (
                <div key={index}>
                  {/* Sender name */}
                  <p
                    className={`text-xs mb-1 ${
                      message.role === "user"
                        ? "text-right text-gray-500"
                        : "text-left text-green-600"
                    }`}
                  >
                    {message.role === "user" ? "User" : "People AI"}
                  </p>
                  {/* Message bubble */}
                  <div
                    className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[90%] px-4 py-3 rounded-2xl ${
                        message.role === "user"
                          ? "bg-gray-200 text-gray-900 rounded-br-md"
                          : "bg-green-600 text-white rounded-bl-md"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right column: Debrief transcript */}
        <div className="flex flex-col min-h-0">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 pb-2 border-b border-teal-200 flex-shrink-0">
            {t("statsNew.interaction.peopleAI.debriefTranscript")}
          </h4>
          <div className="border-2 border-teal-200 rounded-lg p-4 flex-1 overflow-y-auto">
            {!hasDebrief ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-lg text-gray-400 font-medium">N/A</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Initial debrief - special styling with markdown */}
                {initialDebrief && (
                  <div className="mb-4">
                    <p className="text-xs text-green-700 mb-1 font-semibold">
                      {t("statsNew.interaction.peopleAI.initialDebrief")}
                    </p>
                    <div className="bg-green-100 border-2 border-green-400 rounded-lg px-4 py-3 prose prose-sm prose-gray max-w-none text-sm [&>p]:text-sm [&>ul]:text-sm [&>ol]:text-sm [&>li]:text-sm">
                      <ReactMarkdown>{initialDebrief}</ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Follow-up conversation - same style as exchange */}
                {followUpMessages.map((message, index) => (
                  <div key={index}>
                    {/* Sender name */}
                    <p
                      className={`text-xs mb-1 ${
                        message.role === "user"
                          ? "text-right text-gray-500"
                          : "text-left text-green-600"
                      }`}
                    >
                      {message.role === "user" ? "User" : "Debriefing AI"}
                    </p>
                    {/* Message bubble - same style as exchange */}
                    <div
                      className={`flex ${
                        message.role === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[90%] px-4 py-3 rounded-2xl ${
                          message.role === "user"
                            ? "bg-gray-200 text-gray-900 rounded-br-md"
                            : "bg-green-600 text-white rounded-bl-md"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeopleAIDetail;
